import {
  AlertCircle,
  ClipboardList,
  Minus,
  Plus,
  Search,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { EmptyState } from '../../shared/components/EmptyState';
import { SkeletonCard } from '../../shared/components/Skeleton';
import { formatBRL } from '../../shared/lib/format';
import { useAuthStore } from '../../store/authStore';
import { useCaixaAtual } from '../caixa/hooks';
import { useComandas, useAdicionarItensComanda } from '../comandas/hooks';
import { useCategorias, useProdutos } from '../produtos/hooks';
import type { Produto } from '../produtos/types';
import { ItemSelectorModal } from './components/ItemSelectorModal';
import { PagamentoModal } from './components/PagamentoModal';
import { PesoModal } from './components/PesoModal';
import { ReciboModal } from './components/ReciboModal';
import { useRegistrarVenda } from './hooks';
import { calcularSubtotal, usePdvStore } from './pdvStore';
import type { CartItem, PagamentoItem, RegistrarVendaResponse } from './types';

export function PdvPage() {
  const user = useAuthStore((s) => s.user);
  const { data: caixa, isLoading: loadingCaixa } = useCaixaAtual();

  const [q, setQ] = useState('');
  const [catId, setCatId] = useState<string>('');

  const { data: categorias } = useCategorias(true);
  const { data: produtos, isLoading } = useProdutos({
    q: q.trim() || undefined,
    categoria_id: catId || undefined,
    ativo: true,
  });

  const {
    itens,
    comandaId,
    addItem,
    removeItem,
    updateQuantidade,
    setComandaId,
    clear,
    total,
  } = usePdvStore();

  const { data: comandasAbertas } = useComandas({ status: 'aberta' });
  const comandaSelecionada = (comandasAbertas ?? []).find((c) => c.id === comandaId) ?? null;

  const [selectorProduto, setSelectorProduto] = useState<Produto | null>(null);
  const [pesoProduto, setPesoProduto] = useState<Produto | null>(null);
  const [pagamentoOpen, setPagamentoOpen] = useState(false);

  const [lastVenda, setLastVenda] = useState<RegistrarVendaResponse | null>(null);
  const [lastItens, setLastItens] = useState<CartItem[]>([]);
  const [lastPagamentos, setLastPagamentos] = useState<PagamentoItem[]>([]);

  const registrarMut = useRegistrarVenda();
  const adicionarItensMut = useAdicionarItensComanda();

  const caixaAberto = !!caixa;

  const handleProdutoClick = (p: Produto) => {
    if (!caixaAberto) {
      toast.warning('Abra o caixa antes de vender.');
      return;
    }
    if (p.tipo === 'peso') {
      setPesoProduto(p);
      return;
    }
    // se unitario sem tamanhos e sem grupos, adiciona direto
    if (p.tamanhos.length === 0 && p.grupos.length === 0 && Number(p.preco_unitario) > 0) {
      addItem({
        uid: Math.random().toString(36).slice(2),
        produto: p,
        tamanho_id: null,
        tamanho_nome: null,
        quantidade: 1,
        preco_base: Number(p.preco_unitario),
        complementos: [],
      });
      return;
    }
    setSelectorProduto(p);
  };

  const totalVal = total();

  // F2 abre busca, ESC limpa selecao, F8 abre pagamento
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        const el = document.getElementById('pdv-search') as HTMLInputElement | null;
        el?.focus();
        el?.select();
      } else if (e.key === 'F8' && itens.length > 0) {
        e.preventDefault();
        setPagamentoOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [itens.length]);

  const handlePagar = async (pagamentos: PagamentoItem[]) => {
    const payload = {
      desconto: 0,
      comanda_id: comandaId,
      observacao: usePdvStore.getState().observacao || undefined,
      itens: itens.map((i) => {
        const complTotal = i.complementos.reduce((a, c) => a + c.preco_adicional, 0);
        return {
          produto_id: i.produto.id,
          produto_nome: i.produto.nome,
          tamanho_id: i.tamanho_id,
          tamanho_nome: i.tamanho_nome,
          tipo: i.produto.tipo,
          quantidade: i.quantidade,
          preco_base: i.preco_base,
          preco_complementos: complTotal,
          subtotal: calcularSubtotal(i),
          complementos: i.complementos,
          observacao: i.observacao,
        };
      }),
      pagamentos,
    };

    const res = await registrarMut.mutateAsync(payload);
    setLastVenda(res);
    setLastItens([...itens]);
    setLastPagamentos(pagamentos);
    setPagamentoOpen(false);
    clear();
    toast.success(`Venda #${res.numero} registrada`);
  };

  const handleLancarNaComanda = async () => {
    if (!comandaId) return;
    const payload = {
      itens: itens.map((i) => {
        const complTotal = i.complementos.reduce((a, c) => a + c.preco_adicional, 0);
        return {
          produto_id: i.produto.id,
          produto_nome: i.produto.nome,
          tamanho_id: i.tamanho_id,
          tamanho_nome: i.tamanho_nome,
          tipo: i.produto.tipo,
          quantidade: i.quantidade,
          preco_base: i.preco_base,
          preco_complementos: complTotal,
          subtotal: calcularSubtotal(i),
          complementos: i.complementos,
          observacao: i.observacao,
        };
      }),
    };
    await adicionarItensMut.mutateAsync({ id: comandaId, body: payload });
    clear();
  };

  const filtered = useMemo(() => produtos ?? [], [produtos]);

  return (
    <div className="flex h-[calc(100dvh-96px)] flex-col gap-4 lg:h-[calc(100dvh-80px)] lg:flex-row">
      {/* Grid de produtos */}
      <section className="flex min-h-0 flex-1 flex-col">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id="pdv-search"
              type="search"
              className="input pl-9"
              placeholder="Buscar produto (F2) ou bipar codigo de barras…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setCatId('')}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm ${
              catId === ''
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-700 hover:bg-slate-100'
            }`}
          >
            Todas
          </button>
          {(categorias ?? []).map((c) => (
            <button
              key={c.id}
              onClick={() => setCatId(c.id)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm ${
                catId === c.id
                  ? 'text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
              style={catId === c.id ? { backgroundColor: c.cor } : undefined}
            >
              {c.nome}
            </button>
          ))}
        </div>

        {!loadingCaixa && !caixaAberto && (
          <div className="mb-3 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Caixa fechado</p>
              <p className="text-xs">Abra o caixa antes de iniciar as vendas.</p>
            </div>
            <Link to="/caixa" className="btn-primary text-xs">
              <Wallet className="h-4 w-4" />
              Abrir caixa
            </Link>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Search}
              title="Nenhum produto encontrado"
              description="Verifique a busca ou cadastre produtos."
            />
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((p) => {
                const display =
                  p.tipo === 'peso'
                    ? `${formatBRL(Number(p.preco_por_kg ?? 0))}/kg`
                    : p.tamanhos.length > 0
                      ? `a partir ${formatBRL(Math.min(...p.tamanhos.map((t) => Number(t.preco))))}`
                      : formatBRL(Number(p.preco_unitario ?? 0));
                return (
                  <button
                    key={p.id}
                    onClick={() => handleProdutoClick(p)}
                    className="card group flex flex-col p-3 text-left transition hover:shadow-md active:scale-[0.98]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      {p.categoria && (
                        <span
                          className="mt-0.5 inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: p.categoria.cor }}
                        />
                      )}
                      {p.tipo === 'peso' && (
                        <span className="rounded-full bg-amber-100 px-1.5 text-[10px] font-medium text-amber-800">
                          Peso
                        </span>
                      )}
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm font-medium text-slate-900">
                      {p.nome}
                    </p>
                    <p className="mt-auto pt-2 text-base font-bold text-brand-700">
                      {display}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Carrinho */}
      <aside className="flex min-h-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm lg:w-[360px]">
        <header className="flex items-center justify-between border-b border-slate-200 p-3">
          <h2 className="font-semibold text-slate-900">
            Carrinho{' '}
            <span className="ml-1 text-xs font-normal text-slate-500">
              ({itens.length} {itens.length === 1 ? 'item' : 'itens'})
            </span>
          </h2>
          {itens.length > 0 && (
            <button
              type="button"
              onClick={clear}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Limpar"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </header>

        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
          <ClipboardList className="h-4 w-4 text-slate-400" />
          <select
            className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-sm"
            value={comandaId ?? ''}
            onChange={(e) => setComandaId(e.target.value || null)}
            aria-label="Vincular comanda"
          >
            <option value="">Sem comanda (venda avulsa)</option>
            {(comandasAbertas ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                #{c.numero} {c.cliente_nome ? `- ${c.cliente_nome}` : c.mesa ? `- Mesa ${c.mesa}` : ''}
              </option>
            ))}
          </select>
          {comandaSelecionada && (
            <button
              type="button"
              className="rounded p-1 text-slate-400 hover:bg-slate-100"
              onClick={() => setComandaId(null)}
              aria-label="Limpar comanda"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {itens.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">
              Selecione produtos a esquerda para adicionar ao carrinho.
            </p>
          ) : (
            <ul className="space-y-2">
              {itens.map((i) => {
                const sub = calcularSubtotal(i);
                return (
                  <li key={i.uid} className="rounded-lg border border-slate-200 p-2 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900">
                          {i.produto.nome}
                          {i.tamanho_nome ? ` · ${i.tamanho_nome}` : ''}
                        </p>
                        {i.complementos.length > 0 && (
                          <p className="line-clamp-1 text-[11px] text-slate-500">
                            {i.complementos.map((c) => c.nome).join(', ')}
                          </p>
                        )}
                        {i.observacao && (
                          <p className="text-[11px] italic text-slate-500">
                            &quot;{i.observacao}&quot;
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeItem(i.uid)}
                        className="rounded p-1 text-red-500 hover:bg-red-50"
                        aria-label="Remover"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        {i.produto.tipo === 'peso' ? (
                          <span className="text-xs text-slate-600">
                            {i.quantidade.toFixed(3)} kg
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                updateQuantidade(i.uid, Math.max(1, i.quantidade - 1))
                              }
                              className="h-6 w-6 rounded border border-slate-300 text-sm hover:bg-slate-50"
                            >
                              <Minus className="mx-auto h-3 w-3" />
                            </button>
                            <span className="min-w-[2ch] text-center text-sm font-medium">
                              {i.quantidade}
                            </span>
                            <button
                              onClick={() => updateQuantidade(i.uid, i.quantidade + 1)}
                              className="h-6 w-6 rounded border border-slate-300 text-sm hover:bg-slate-50"
                            >
                              <Plus className="mx-auto h-3 w-3" />
                            </button>
                          </>
                        )}
                      </div>
                      <span className="font-semibold text-slate-900">{formatBRL(sub)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="space-y-3 border-t border-slate-200 p-3">
          <div className="flex justify-between border-slate-200 pt-2 text-lg font-bold text-brand-700">
            <span>Total</span>
            <span>{formatBRL(totalVal)}</span>
          </div>
          {comandaId ? (
            <div className="flex gap-2">
              <button
                className="btn-outline flex-1 text-sm border-brand-600 text-brand-700 hover:bg-brand-50"
                disabled={itens.length === 0 || !caixaAberto || adicionarItensMut.isPending}
                onClick={handleLancarNaComanda}
              >
                Lançar na Comanda
              </button>
              <button
                className="btn-primary flex-1 text-sm"
                disabled={!caixaAberto}
                onClick={() => setPagamentoOpen(true)}
              >
                Cobrar (F8)
              </button>
            </div>
          ) : (
            <button
              className="btn-primary w-full text-base"
              disabled={itens.length === 0 || !caixaAberto}
              onClick={() => setPagamentoOpen(true)}
              title="F8"
            >
              Cobrar (F8)
            </button>
          )}
        </footer>
      </aside>

      <ItemSelectorModal
        open={!!selectorProduto}
        produto={selectorProduto}
        onClose={() => setSelectorProduto(null)}
        onConfirm={(item) => {
          addItem(item);
          setSelectorProduto(null);
        }}
      />

      <PesoModal
        open={!!pesoProduto}
        produto={pesoProduto}
        onClose={() => setPesoProduto(null)}
        onConfirm={(item) => {
          addItem(item);
          setPesoProduto(null);
        }}
      />

      <PagamentoModal
        open={pagamentoOpen}
        total={totalVal}
        onClose={() => setPagamentoOpen(false)}
        onConfirm={handlePagar}
        loading={registrarMut.isPending}
      />

      <ReciboModal
        open={!!lastVenda}
        venda={lastVenda}
        itens={lastItens}
        pagamentos={lastPagamentos}
        operadorNome={user?.nome ?? user?.email ?? 'Operador'}
        onClose={() => setLastVenda(null)}
      />
    </div>
  );
}
